import React from "react";
import {
  AppBar,
  Box,
  Container,
  Divider,
  Link,
  Paper,
  Toolbar,
  Typography
} from "@mui/material";
import {styled} from "@mui/material/styles";
import Footer from "../components/footer";
import LogoImg from "../assets/images/logo.svg";

const Logo = styled('img')(({theme}) => ({
  maxHeight: '30px',
  alignSelf: 'center',
  display: 'flex',
  flex: 1
}));

const StyledPaper = styled(Paper)(({theme}) => ({
  padding: '24px',
  marginTop: '18px',
  marginBottom: '18px'
}));

const StyledDivider = styled(Divider)(({theme}) => ({
  marginTop: '18px',
  marginBottom: '18px'
}));

const Section = ({title, children}) => (
  <Box component="section" sx={{mb: 3}}>
    <Typography variant="h5" component="h2" gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="lg">
      <AppBar position="static">
        <Toolbar>
          <Link href="/" aria-label="SyncBin home" sx={{display: 'flex', flexGrow: 1}}>
            <Logo src={LogoImg} alt="SyncBin"/>
          </Link>
        </Toolbar>
      </AppBar>

      <StyledPaper elevation={3}>
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1" component="p" gutterBottom>
          Effective date: May 30, 2026
        </Typography>
        <Typography variant="body1" component="p">
          This Privacy Policy applies to SyncBin, including the SyncBin Android app with package name
          com.syncbin, the SyncBin iOS app, and the SyncBin web app at thecaringdeveloper.com. SyncBin is
          provided by Roland Treiber, also publishing as The Caring Developer.
        </Typography>

        <StyledDivider/>

        <Section title="What SyncBin does">
          <Typography variant="body1" component="p">
            SyncBin is a real-time sharing board for moving plain text and files between your own devices.
            The app creates or accepts a session ID, then lets devices using that same session see the same
            text and file list. You can also copy a session link or show a QR code to open the same session
            on another device.
          </Typography>
        </Section>

        <Section title="Information you provide">
          <Typography variant="body1" component="p">
            SyncBin stores the text that you type or paste into a session and the files that you upload to a
            session. File names are stored so the app can show the file list. SyncBin does not require an
            account, sign-in, profile, name, email address, or phone number.
          </Typography>
        </Section>

        <Section title="Information stored on your device">
          <Typography variant="body1" component="p">
            If you use quick access, SyncBin stores the saved session IDs locally on your device or in your
            browser so you can return to those sessions more easily. Removing a quick-access entry deletes
            that local shortcut; it does not by itself delete the session data stored for that session.
          </Typography>
        </Section>

        <Section title="How information is used">
          <Typography variant="body1" component="p">
            Session text, uploaded files, file names, and session IDs are used to provide the SyncBin sharing
            service, keep devices in the same session synchronized, preview images, download uploaded files,
            and delete files when requested. SyncBin does not sell personal information, show ads, or use
            your session content for advertising.
          </Typography>
        </Section>

        <Section title="Third-party services">
          <Typography variant="body1" component="p">
            SyncBin uses Google Firebase services to operate the product. Firebase Realtime Database stores
            session text and file lists. Firebase Cloud Storage stores uploaded files. These services may
            process technical information such as IP address, device information, app or browser information,
            and service logs as needed to provide, secure, and maintain the service.
          </Typography>
        </Section>

        <Section title="Sharing and access">
          <Typography variant="body1" component="p">
            Anyone who knows or can guess a session ID can access the content in that session. Do not place
            passwords, private keys, financial information, health information, or other sensitive content in
            SyncBin. Session data and files are not end-to-end encrypted by SyncBin.
          </Typography>
        </Section>

        <Section title="Retention and deletion">
          <Typography variant="body1" component="p">
            The service is designed for temporary sharing. You can clear text by deleting it from the text
            field, and you can delete uploaded files from the file list. SyncBin sessions are also designed
            to be cleared routinely every Sunday at midnight GMT+1. Deletion from active storage may not
            immediately remove information from provider backups or operational logs.
          </Typography>
        </Section>

        <Section title="Children">
          <Typography variant="body1" component="p">
            SyncBin is not directed to children under 13. Because the app does not require accounts, Roland
            Treiber does not knowingly collect contact details or profile information from children.
          </Typography>
        </Section>

        <Section title="Changes to this policy">
          <Typography variant="body1" component="p">
            This policy may be updated when SyncBin changes or when legal, platform, or operational
            requirements change. The effective date above will be updated when material changes are made.
          </Typography>
        </Section>

        <Section title="Contact">
          <Typography variant="body1" component="p">
            If you have questions about this Privacy Policy or SyncBin privacy practices, contact Roland
            Treiber / The Caring Developer at{' '}
            <Link href="mailto:fluentechltd@gmail.com">
              fluentechltd@gmail.com
            </Link>
            {' '}or through{' '}
            <Link href="https://thecaringdeveloper.com" rel="noreferrer" target="_blank">
              thecaringdeveloper.com
            </Link>.
          </Typography>
        </Section>
      </StyledPaper>

      <Footer/>
    </Container>
  )
}

export default PrivacyPolicy;
